import React, { useState } from "react";

const C = {
  bg:"#06080F", surface:"#0e1117", card:"#141820", border:"#1e2533",
  accent:"#F59E0B", accentDim:"#F59E0B22", text:"#F1F5F9", muted:"#64748B",
  danger:"#EF4444", success:"#22C55E", purple:"#8B5CF6",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function pct(n, total) {
  if (!total) return 0;
  return Math.round((n / total) * 100);
}

function Bar({ value, max, color = C.accent }) {
  const w = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ height: 4, borderRadius: 2, background: C.border, marginTop: 4, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${w}%`, background: color, borderRadius: 2, transition: "width 0.3s" }} />
    </div>
  );
}

function Pill({ label, color, bg }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99, background: bg || color + "22", color: color, letterSpacing: 0.4 }}>
      {label}
    </span>
  );
}

const DIM_LABELS = {
  ageBands: "Age Band", genders: "Gender", cityTiers: "City Tier", languages: "Language",
  education: "Education", incomeBands: "Income Band", professions: "Profession",
  householdRoles: "Household Role", yearsWorking: "Years Working", industries: "Industry",
};

const SKIP_REASON_LABEL = {
  no_candidates: "No candidates passed targeting filters",
  no_tier2_candidates: "All candidates were Tier 1 or Tier 3 — no borderline matches to re-score",
  no_api_key: "Anthropic API key not configured",
  error: "API call failed — targeting scores used as fallback",
};

// ── Sub-sections ──────────────────────────────────────────────────────────────

function SectionHeader({ icon, title, badge }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
      <span style={{ fontSize: 13 }}>{icon}</span>
      <span style={{ fontWeight: 700, fontSize: 12, color: C.text, textTransform: "uppercase", letterSpacing: 0.6 }}>{title}</span>
      {badge != null && (
        <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: C.muted }}>{badge}</span>
      )}
    </div>
  );
}

function PoolSection({ pool }) {
  if (!pool) return null;
  const rows = [
    { label: "Verified participants",     value: pool.verified,      color: C.text },
    { label: "With complete profiles",    value: pool.withProfile,   color: C.text },
    { label: "No profile (excluded)",     value: pool.noProfile,     color: pool.noProfile > 0 ? C.muted : C.muted },
    { label: "Already invited (skipped)", value: pool.alreadyInvited, color: pool.alreadyInvited > 0 ? "#F59E0B" : C.muted },
    { label: "Eligible for scoring",      value: pool.eligible,      color: C.success, bold: true },
  ];
  return (
    <div>
      <SectionHeader icon="👥" title="Participant Pool" />
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {rows.map(r => (
          <div key={r.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.muted }}>
            <span>{r.label}</span>
            <strong style={{ color: r.color, fontWeight: r.bold ? 700 : 600 }}>{r.value?.toLocaleString()}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function PipelineSection({ pool, exclusions, total }) {
  if (!exclusions || !pool) return null;
  const eligible = pool.eligible || 0;
  const matched = total || 0;
  const steps = [
    { label: "Eligible for scoring", value: eligible, color: C.text },
    { label: "Excluded — filter mismatch", value: exclusions.byFilterMismatch, color: C.danger, note: "No demographic/profile dimension matched" },
    { label: "Excluded — high pending load", value: exclusions.byFatigue, color: "#F59E0B", note: ">3 pending invitations across all surveys" },
    { label: "Excluded — score below threshold", value: exclusions.byLowScore, color: C.muted, note: "Final score < 40% even after engagement adjustment" },
    { label: "Qualified candidates", value: matched, color: C.success, bold: true },
  ];
  return (
    <div>
      <SectionHeader icon="⚙️" title="Scoring Pipeline" />
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {steps.map((s, i) => (
          <div key={i}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", fontSize: 12 }}>
              <div>
                <span style={{ color: s.bold ? C.text : C.muted }}>{s.label}</span>
                {s.note && <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>{s.note}</div>}
              </div>
              <strong style={{ color: s.color, fontWeight: s.bold ? 700 : 600, flexShrink: 0, marginLeft: 8 }}>
                {s.value?.toLocaleString()}
                {eligible > 0 && s.value > 0 && !s.bold && (
                  <span style={{ fontWeight: 400, color: C.muted, marginLeft: 4 }}>({pct(s.value, eligible)}%)</span>
                )}
              </strong>
            </div>
            {eligible > 0 && (
              <Bar value={s.bold ? matched : s.value} max={eligible}
                color={s.bold ? C.success : s.color} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FiltersSection({ filtersApplied, dimensionBreakdown, pool }) {
  const [expanded, setExpanded] = useState({});
  if (!filtersApplied?.length) return (
    <div>
      <SectionHeader icon="🎯" title="Targeting Filters" badge="None" />
      <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>
        No demographic or profile filters set — all verified participants with complete profiles are eligible.
      </div>
    </div>
  );

  return (
    <div>
      <SectionHeader icon="🎯" title="Targeting Filters" badge={`${filtersApplied.length} active`} />
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtersApplied.map(({ dimension, values }) => {
          const info = dimensionBreakdown?.[dimension];
          const matchCount = info?.matchCount || 0;
          const total = pool?.withProfile || 1;
          const isOpen = expanded[dimension];
          const hasMatch = matchCount > 0;
          return (
            <div key={dimension} style={{ borderRadius: 8, border: `1px solid ${hasMatch ? C.success + "55" : C.danger + "55"}`, overflow: "hidden" }}>
              {/* Header row */}
              <div
                onClick={() => setExpanded(e => ({ ...e, [dimension]: !e[dimension] }))}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", cursor: "pointer", background: C.surface, userSelect: "none" }}
              >
                <span style={{ fontSize: 11, fontWeight: 700, color: hasMatch ? C.success : C.danger }}>
                  {hasMatch ? "✓" : "✗"}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{DIM_LABELS[dimension] || dimension}</div>
                  <div style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {values.join(" · ")}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: hasMatch ? C.success : C.danger }}>
                    {matchCount.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 10, color: C.muted }}>{pct(matchCount, total)}% of pool</div>
                </div>
                <span style={{ color: C.muted, fontSize: 12, marginLeft: 4 }}>{isOpen ? "▲" : "▼"}</span>
              </div>

              {/* Expanded value breakdown */}
              {isOpen && info && (
                <div style={{ padding: "10px 12px", borderTop: `1px solid ${C.border}` }}>
                  {info.noData > 0 && (
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 7 }}>
                      ⚠ {info.noData.toLocaleString()} participants have no value for this field
                    </div>
                  )}
                  {info.topValues.length === 0 ? (
                    <div style={{ fontSize: 11, color: C.danger }}>No participants have data for this dimension</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {info.topValues.map(({ value, count, pct: p, matches }) => (
                        <div key={value}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                            <span style={{ color: matches ? C.success : C.muted, fontWeight: matches ? 600 : 400 }}>
                              {matches ? "✓ " : ""}{value}
                            </span>
                            <span style={{ color: C.muted }}>{count.toLocaleString()} ({p}%)</span>
                          </div>
                          <Bar value={count} max={info.totalWithData} color={matches ? C.success : C.border} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EngagementSection({ engagementDistribution }) {
  if (!engagementDistribution) return null;
  const { none, low, medium, high } = engagementDistribution;
  const total = none + low + medium + high;
  const rows = [
    { label: "No pending invitations", value: none, color: C.success, note: "0 pending" },
    { label: "Low load", value: low, color: "#22D3EE", note: "1–3 pending" },
    { label: "Medium load", value: medium, color: "#F59E0B", note: "4–6 pending" },
    { label: "High load", value: high, color: C.danger, note: "7–10 pending" },
  ];
  return (
    <div>
      <SectionHeader icon="📊" title="Participant Engagement Load" badge={`${total.toLocaleString()} scored`} />
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map(r => (
          <div key={r.label}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
              <span style={{ color: C.muted }}>{r.label} <span style={{ fontSize: 10 }}>({r.note})</span></span>
              <span style={{ color: r.color, fontWeight: 600 }}>{r.value.toLocaleString()} ({pct(r.value, total)}%)</span>
            </div>
            <Bar value={r.value} max={total} color={r.color} />
          </div>
        ))}
      </div>
    </div>
  );
}

function SemanticSection({ semanticScoring, semanticScored }) {
  if (!semanticScoring) return null;
  const { skipped, skipReason, eligible, rescored, promoted, demoted, excluded } = semanticScoring;
  return (
    <div>
      <SectionHeader icon="🤖" title="AI Semantic Scoring" />
      {skipped ? (
        <div style={{ fontSize: 12, color: C.muted }}>
          <Pill label="SKIPPED" color={C.muted} />
          <span style={{ marginLeft: 8 }}>{SKIP_REASON_LABEL[skipReason] || skipReason}</span>
          <div style={{ marginTop: 6, fontSize: 11, lineHeight: 1.5 }}>
            AI semantic scoring activates for Tier 2 (borderline) candidates only. It sends each candidate's profile to Claude, which scores the fit against the survey's description and purpose on a 0–10 scale, then blends it with the targeting score (60/40 split).
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            <Pill label="RAN" color={C.success} />
            <Pill label={`${eligible} eligible`} color={C.muted} />
            <Pill label={`${rescored} re-scored`} color={C.accent} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 12, color: C.muted }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Promoted Tier 2 → Tier 1</span>
              <strong style={{ color: C.success }}>{promoted}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Remained Tier 2</span>
              <strong style={{ color: C.accent }}>{rescored - promoted - demoted - excluded}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Demoted Tier 2 → Tier 3</span>
              <strong style={{ color: C.muted }}>{demoted}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Excluded (score too low)</span>
              <strong style={{ color: C.danger }}>{excluded}</strong>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Analysis panel (collapsible) ──────────────────────────────────────────────

function AnalysisPanel({ analysis, total, semanticScored }) {
  const [open, setOpen] = useState(false);
  if (!analysis) return null;
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 14px", background: C.surface, border: "none", cursor: "pointer", fontFamily: "inherit" }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>🔍 Matching Analysis</span>
        <span style={{ fontSize: 11, color: C.muted }}>{open ? "Hide ▲" : "Show ▼"}</span>
      </button>

      {open && (
        <div style={{ padding: "16px 14px", display: "flex", flexDirection: "column", gap: 20, borderTop: `1px solid ${C.border}` }}>
          <PoolSection pool={analysis.pool} />
          <div style={{ height: 1, background: C.border }} />
          <PipelineSection pool={analysis.pool} exclusions={analysis.exclusions} total={total} />
          {analysis.filtersApplied?.length > 0 && <>
            <div style={{ height: 1, background: C.border }} />
            <FiltersSection
              filtersApplied={analysis.filtersApplied}
              dimensionBreakdown={analysis.dimensionBreakdown}
              pool={analysis.pool}
            />
          </>}
          <div style={{ height: 1, background: C.border }} />
          <EngagementSection engagementDistribution={analysis.engagementDistribution} />
          <div style={{ height: 1, background: C.border }} />
          <SemanticSection semanticScoring={analysis.semanticScoring} semanticScored={semanticScored} />
          {analysis.sampleSizeCap && (
            <div style={{ fontSize: 11, color: C.muted, borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: -8 }}>
              Selection capped at {analysis.sampleSizeCap.toLocaleString()} candidates (2× target sample size of {(analysis.sampleSizeCap / 2).toLocaleString()})
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────

export default function InviteMatchingModal({
  // State
  preview,       // null | "loading" | { total, tiers, semanticScored, analysis }
  inviting,      // bool
  result,        // null | { invited, skippedFatigue, skippedQuota, roundStart, roundEnd, tiers }
  error,         // string | null
  subtitle,      // string shown under title
  rankingReady,  // bool — false → show "compute ranking first" instead of Send button
  // Actions
  onCancel,      // () => void
  onConfirm,     // () => void — sends invitations (only callable when rankingReady)
  cancelLabel,   // optional override for cancel button text
}) {
  const tiers = preview?.tiers || {};

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000c", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
        width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ padding: "20px 20px 0" }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 3 }}>✉ Invite Participants</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 18 }}>
            {subtitle || (preview?.semanticScored ? "Matching used AI semantic scoring for borderline candidates." : "Matching ran targeting + engagement scoring.")}
          </div>
        </div>

        <div style={{ padding: "0 20px 20px", flex: 1 }}>
          {/* Loading */}
          {preview === "loading" && (
            <div style={{ textAlign: "center", padding: "40px 0", color: C.muted, fontSize: 13 }}>
              <div style={{ fontSize: 24, marginBottom: 12 }}>⏳</div>
              Analysing participant pool…
            </div>
          )}

          {/* Result screen */}
          {result && (
            <div style={{ padding: 18, borderRadius: 10, marginBottom: 16,
              background: result.invited > 0 ? "#22C55E18" : "#F59E0B18",
              border: `1px solid ${result.invited > 0 ? "#22C55E55" : "#F59E0B55"}` }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: result.invited > 0 ? C.success : "#F59E0B", marginBottom: 8 }}>
                {result.invited > 0 ? `✓ ${result.invited.toLocaleString()} invitation${result.invited !== 1 ? "s" : ""} sent` : "No invitations sent"}
              </div>

              {/* Ranking-based: rank range */}
              {result.roundStart != null && (
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>
                  Selected from ranking positions #{result.roundStart}–#{result.roundEnd}
                </div>
              )}

              {/* Legacy path: already invited count */}
              {result.alreadyInvited > 0 && (
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>
                  {result.alreadyInvited} participant{result.alreadyInvited !== 1 ? "s" : ""} were already invited
                </div>
              )}

              {/* Tier breakdown (ranking path) */}
              {result.tiers && (result.tiers.tier1 > 0 || result.tiers.tier2 > 0 || result.tiers.tier3 > 0) && (
                <div style={{ display: "flex", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                  {result.tiers.tier1 > 0 && <Pill label={`T1: ${result.tiers.tier1}`} color={C.success} />}
                  {result.tiers.tier2 > 0 && <Pill label={`T2: ${result.tiers.tier2}`} color={C.accent} />}
                  {result.tiers.tier3 > 0 && <Pill label={`T3: ${result.tiers.tier3}`} color={C.muted} />}
                </div>
              )}

              {/* Skipped reasons */}
              {(result.skippedFatigue > 0 || result.skippedQuota > 0) && (
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
                  {result.skippedFatigue > 0 && (
                    <div style={{ fontSize: 11, color: "#F59E0B" }}>
                      ⚠ {result.skippedFatigue} skipped — fatigue cap (3+ pending invites)
                    </div>
                  )}
                  {result.skippedQuota > 0 && (
                    <div style={{ fontSize: 11, color: "#F59E0B" }}>
                      ⚠ {result.skippedQuota} skipped — segment quota full
                    </div>
                  )}
                </div>
              )}

              <div style={{ fontSize: 12, color: C.muted }}>
                {result.invited > 0
                  ? "Participants have been notified and will see this survey in their dashboard."
                  : "No participants matched your targeting criteria. Open the Matching Analysis above to diagnose."}
              </div>
            </div>
          )}

          {/* Preview content */}
          {preview && preview !== "loading" && !result && (<>
            {/* Tier cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 16 }}>
              {[
                { label: "Tier 1", sub: "Score ≥ 85%", desc: "Perfect match",  count: tiers.tier1 || 0, color: C.success },
                { label: "Tier 2", sub: "Score 60–84%", desc: "Good match",    count: tiers.tier2 || 0, color: C.accent },
                { label: "Tier 3", sub: "Score 40–59%", desc: "Broad match",   count: tiers.tier3 || 0, color: "#64748B" },
              ].map(t => (
                <div key={t.label} style={{ background: C.surface, borderRadius: 8, padding: "12px 14px", border: `1px solid ${t.color}33` }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: t.color, textTransform: "uppercase", letterSpacing: 0.5 }}>{t.label}</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: C.text, margin: "4px 0" }}>{(t.count || 0).toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: C.muted }}>{t.sub}</div>
                  <div style={{ fontSize: 10, color: C.muted }}>{t.desc}</div>
                </div>
              ))}
            </div>

            {/* Analysis panel */}
            <AnalysisPanel analysis={preview.analysis} total={preview.total} semanticScored={preview.semanticScored} />

            {/* Summary line */}
            <div style={{ fontSize: 13, color: C.text, marginBottom: preview.total === 0 ? 4 : 16 }}>
              <strong>{(preview.total || 0).toLocaleString()}</strong> participant{preview.total !== 1 ? "s" : ""} {rankingReady ? "will receive invitations" : "match targeting criteria"}.
            </div>
            {preview.total === 0 && (
              <div style={{ fontSize: 12, color: "#F59E0B", marginBottom: 16, lineHeight: 1.5 }}>
                Open "Matching Analysis" above to see exactly why participants were excluded.
              </div>
            )}
          </>)}

          {/* Error */}
          {error && (
            <div style={{ color: C.danger, fontSize: 13, marginBottom: 14, padding: "10px 12px",
              background: C.danger + "18", borderRadius: 8, border: `1px solid ${C.danger}55` }}>
              {error}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button onClick={onCancel}
              style={{ padding: "9px 18px", borderRadius: 8, background: "transparent",
                border: `1px solid ${C.border}`, color: C.muted, fontSize: 13,
                cursor: "pointer", fontFamily: "inherit", minWidth: 80 }}>
              {result ? "Done" : (cancelLabel || "Cancel")}
            </button>
            {!result && preview && preview !== "loading" && (
              rankingReady ? (
                <button onClick={onConfirm} disabled={inviting || !preview.total}
                  style={{ padding: "9px 20px", borderRadius: 8, background: C.accent, color: "#000",
                    fontWeight: 700, fontSize: 13, border: "none", cursor: inviting || !preview.total ? "not-allowed" : "pointer",
                    fontFamily: "inherit", opacity: inviting || !preview.total ? 0.5 : 1, minWidth: 120 }}>
                  {inviting ? "Sending…" : `Send ${(preview.total || 0).toLocaleString()} Invitation${preview.total !== 1 ? "s" : ""}`}
                </button>
              ) : (
                <div style={{ fontSize: 12, color: "#F59E0B", padding: "9px 14px", borderRadius: 8,
                  background: "#F59E0B18", border: "1px solid #F59E0B44", alignSelf: "center" }}>
                  ⚠ Compute a ranking first to send invitations
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
